import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { transcriptsAPI } from '../services/api';

// Helper to get media URL with auth token
export const getAuthenticatedMediaUrl = (mediaUrl) => {
  if (!mediaUrl) return null;
  const token = localStorage.getItem('token');
  const streamUrl = mediaUrl.replace('/api/files/', '/api/files/stream/');
  return `${streamUrl}?token=${encodeURIComponent(token)}`;
};

export function formatTime(ms) {
  const seconds = Math.floor(ms / 1000);
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export default function useTranscriptViewer(id) {
  const [transcript, setTranscript] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [activeUtteranceIndex, setActiveUtteranceIndex] = useState(-1);
  const mediaPlayerRef = useRef(null);
  const transcriptContainerRef = useRef(null);

  useEffect(() => {
    const loadTranscript = async () => {
      try {
        const response = await transcriptsAPI.get(id);
        setTranscript(response.data);
      } catch (err) {
        setError('Failed to load transcript');
      } finally {
        setLoading(false);
      }
    };

    loadTranscript();
  }, [id]);

  // Update active utterance based on current time
  useEffect(() => {
    if (!transcript?.utterances?.length) return;

    const currentMs = currentTime * 1000;
    const index = transcript.utterances.findIndex((u, i) => {
      const nextStart = transcript.utterances[i + 1]?.start ?? Infinity;
      return currentMs >= u.start && currentMs < nextStart;
    });

    if (index !== activeUtteranceIndex) {
      setActiveUtteranceIndex(index);
    }
  }, [currentTime, transcript, activeUtteranceIndex]);

  const handleTimeUpdate = useCallback((time) => {
    setCurrentTime(time);
  }, []);

  const handleLineClick = useCallback((startMs) => {
    if (mediaPlayerRef.current) {
      mediaPlayerRef.current.seekTo(startMs / 1000);
    }
  }, []);

  const handleDownload = async (format) => {
    try {
      const response = await transcriptsAPI.download(id, format);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = transcript.original_filename.replace(/\.[^/.]+$/, `.${format}`);
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const handleDownloadMedia = () => {
    const token = localStorage.getItem('token');
    const filename = transcript.media_url.replace('/api/files/', '');
    const url = `/api/files/stream/${encodeURIComponent(filename)}?token=${encodeURIComponent(token)}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = transcript.original_filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const copyAllText = () => {
    if (!transcript?.utterances) return;

    const text = transcript.utterances
      .map((u) => `[Speaker ${u.speaker}] ${u.text}`)
      .join('\n\n');

    navigator.clipboard.writeText(text);
  };

  const handleWordClick = useCallback((startMs) => {
    if (mediaPlayerRef.current) {
      mediaPlayerRef.current.seekTo(startMs / 1000);
    }
  }, []);

  // Group utterances by speaker for paragraph display
  const groupedUtterances = useMemo(() => {
    if (!transcript?.utterances) return [];

    const groups = [];
    let currentGroup = null;

    transcript.utterances.forEach((u, index) => {
      // Utterances without word timings get pseudo-words at the utterance
      // start (zero length, so they never show as "spoken") so search
      // highlighting and click-to-seek still work.
      const words = u.words?.length
        ? u.words
        : (u.text || '').split(/\s+/).filter(Boolean).map((text) => ({ text, start: u.start, end: u.start }));

      if (!currentGroup || currentGroup.speaker !== u.speaker) {
        if (currentGroup) groups.push(currentGroup);
        currentGroup = {
          speaker: u.speaker,
          start: u.start,
          end: u.end,
          text: u.text,
          words: [...words],
          utterances: [{ ...u, originalIndex: index }],
        };
      } else {
        currentGroup.text += ' ' + u.text;
        currentGroup.end = u.end;
        currentGroup.words.push(...words);
        currentGroup.utterances.push({ ...u, originalIndex: index });
      }
    });

    if (currentGroup) groups.push(currentGroup);
    return groups;
  }, [transcript]);

  return {
    transcript,
    loading,
    error,
    currentTime,
    activeUtteranceIndex,
    mediaPlayerRef,
    transcriptContainerRef,
    groupedUtterances,
    handleTimeUpdate,
    handleLineClick,
    handleDownload,
    handleDownloadMedia,
    copyAllText,
    handleWordClick,
  };
}
